import * as React from 'react'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import CssBaseline from '@mui/material/CssBaseline'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import { useLogin, useNotify } from 'react-admin'
import AuthProvider from '../../providers/auth/cognitoProvider'
import { useNavigate, useLocation } from 'react-router-dom'

export default function LoginPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const notify = useNotify()
  const login = useLogin()
  const [forgotPassword, setForgotPassword] = React.useState(false)
  const [confirmPassword, setConfirmPassword] = React.useState(false)
  const [changeTempPassword, setChangeTempPassword] = React.useState(false)
  const [mfaRequired, setMfaRequired] = React.useState(false)
  const [mfaSession, setMfaSession] = React.useState(null)
  const [trustDevice, setTrustDevice] = React.useState(false)
  const [accountInfo, setAccountInfo] = React.useState({})

  React.useEffect(() => {
    if (location.pathname === '/login' && localStorage.getItem('idToken') && localStorage.getItem('accessToken')) {
      navigate('/kits')
    }
  }, [location.pathname, navigate])

  const handleSubmit = (event) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    login({
      username: data.get('username'),
      password: data.get('password'),
    }).catch((error) => {
      if (error?.changeTempPassword) {
        setChangeTempPassword(true)
        setAccountInfo({ username: error.user.username, password: error.user.password })
        notify('Please set a new password to continue.')
      } else if (error?.mfaRequired) {
        setMfaRequired(true)
        setMfaSession(error.session)
        setAccountInfo({ username: data.get('username') }) // Keep username for the next step
      } else {
        notify('Invalid username or password.')
      }
    })
  }

  const handleMfaSubmit = (event) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    login({
      mfaCode: data.get('code'),
      session: mfaSession,
      challengeName: 'SOFTWARE_TOKEN_MFA',
      username: accountInfo.username,
      trustDevice: trustDevice,
    }).catch((error) => {
      notify('Invalid MFA code.')
    })
  }

  const handleForgotPassword = (event) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    if (confirmPassword) {
      AuthProvider.confirmPassword({
        username: data.get('username'),
        code: data.get('code'),
        newPassword: data.get('new-password'),
      })
        .then(() => {
          setForgotPassword(false)
        })
        .catch((error) => notify(typeof error === 'string' ? error : error.message || 'An error occurred'))
      return
    }
    AuthProvider.forgotPassword({
      username: data.get('username'),
    })
      .then(() => {
        notify('A confirmation code has been sent to your registered email.')
        setConfirmPassword(true)
      })
      .catch((error) => notify(typeof error === 'string' ? error : error.message || 'An error occurred'))
  }

  const handleTempPasswordReset = (event) => {
    event.preventDefault()
    const data = new FormData(event.currentTarget)

    login({
      newPassword: data.get('new_password'),
      username: accountInfo.username,
      password: accountInfo.password,
    })
      .then(() => {
        notify('Password has been reset successfully.')
        setChangeTempPassword(false)
        setAccountInfo({})
      })
      .catch((error) => notify(error.message || 'An error occurred'))
  }

  return (
    <Container component="main" sx={{ height: '100vh' }} maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography textAlign={'center'} component="h1" variant="h5">
          MaleFromHome Admin
          <br />
          {forgotPassword ? 'Reset Password' : changeTempPassword ? 'Set New Password' : mfaRequired ? 'Enter MFA Code' : 'Sign in'}
        </Typography>
        {changeTempPassword ? (
          <TempPasswordResetForm onSubmit={handleTempPasswordReset} />
        ) : forgotPassword ? (
          <ForgotPasswordForm onSubmit={handleForgotPassword} confirmPassword={confirmPassword} />
        ) : mfaRequired ? (
          <MfaForm onSubmit={handleMfaSubmit} trustDevice={trustDevice} setTrustDevice={setTrustDevice} />
        ) : (
          <SignInForm onSubmit={handleSubmit} setForgotPassword={setForgotPassword} />
        )}
      </Box>
    </Container>
  )
}

const SignInForm = ({ onSubmit, setForgotPassword }) => {
  return (
    <Box component="form" onSubmit={onSubmit} sx={{ mt: 1 }}>
      <TextField margin="normal" required fullWidth id="username" label="Username" name="username" autoFocus />
      <TextField margin="normal" required fullWidth name="password" label="Password" type="password" id="password" />
      <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
        Sign In
      </Button>
      <Grid container>
        <Grid item xs>
          <Button type="button" variant="text" onClick={() => setForgotPassword(true)}>
            Forgot password?
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}

const ForgotPasswordForm = ({ onSubmit, confirmPassword }) => {
  return (
    <Box component="form" onSubmit={onSubmit} sx={{ mt: 1 }}>
      <TextField margin="normal" required fullWidth id="username" label="Username" name="username" autoFocus />

      {confirmPassword ? (
        <>
          <TextField margin="normal" required fullWidth name="code" label="Code" id="code" />
          <TextField
            margin="normal"
            required
            fullWidth
            name="new-password"
            label="New Password"
            type="password"
            id="new-password"
          />
        </>
      ) : (
        <></>
      )}
      <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
        {confirmPassword ? 'Reset Password' : 'Send Code'}
      </Button>
    </Box>
  )
}

const TempPasswordResetForm = ({ onSubmit }) => {
  return (
    <Box component="form" onSubmit={onSubmit} sx={{ mt: 1 }}>
      <TextField
        margin="normal"
        required
        fullWidth
        name="new_password"
        label="New Password"
        type="password"
        id="new_password"
        helperText="Contains at least 1 number, 1 uppercase letter and 1 lowercase letter."
        autoFocus
      />
      <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
        Set New Password
      </Button>
    </Box>
  )
}

const MfaForm = ({ onSubmit, trustDevice, setTrustDevice }) => {
  return (
    <Box component="form" onSubmit={onSubmit} sx={{ mt: 1 }}>
      <TextField
        margin="normal"
        required
        fullWidth
        name="code"
        label="MFA Code"
        type="text"
        id="code"
        autoFocus
        inputProps={{ maxLength: 6 }}
      />
      <FormControlLabel
        control={<Checkbox value="remember" color="primary" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} />}
        label="Trust this device"
      />
      <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
        Verify
      </Button>
    </Box>
  )
}
